import axiosInstance from '../../axiosInstance';
import {
  CompleteSprintPayload,
  CreateSprintPayload,
  DeleteSprintPayload,
  DeleteSprintResult,
  ReorderSprintsPayload,
  Sprint,
  SprintApiResponse,
  SprintList,
  SprintReport,
  SprintStatus,
  SprintWorkItemsPayload,
  SprintWorkload,
  StartSprintPayload,
  UpdateSprintPayload,
} from 'types/project/sprint';
import { PaginatedTasksResponse, Task } from 'types/project/tasks';
import { BusinessError, normalizeApiError } from 'utils/normalizeApiError';

// The backend can answer 200 with { response: { status: false, message } }.
const unwrapSprint = (data: SprintApiResponse): Sprint => {
  const response = data?.response as Sprint & { status?: unknown; message?: string };
  if ((response as any)?.status === false) {
    throw new BusinessError(response.message || 'Something went wrong');
  }
  return response;
};

export const createSprint = async (payload: CreateSprintPayload): Promise<Sprint> => {
  try {
    const { data } = await axiosInstance.post<SprintApiResponse>('/pmo/sprint/create', payload);
    return unwrapSprint(data);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

export const updateSprint = async (payload: UpdateSprintPayload): Promise<Sprint> => {
  try {
    const { data } = await axiosInstance.post<SprintApiResponse>('/pmo/sprint/update', payload);
    return unwrapSprint(data);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * The project's sprints — every one, or only those in `statuses`. An unknown
 * project (or one in another organisation) comes back as an empty body rather
 * than an error.
 */
export const getProjectSprints = async (
  projectUuid: string,
  statuses?: SprintStatus[],
): Promise<SprintList> => {
  try {
    const { data } = await axiosInstance.get<{ response: Partial<SprintList> }>(
      `/pmo/project/${projectUuid}/sprints`,
      { params: statuses?.length ? { status: statuses.join(',') } : undefined },
    );
    return {
      sprints: data?.response?.sprints ?? [],
      nextSprintName: data?.response?.nextSprintName,
    };
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Epics are never in a sprint or the Backlog. The backend leaves them out; this keeps one
 * from showing, or counting, if an old record still has one there.
 */
const isNotEpic = (item: { workTypeCategory?: string | null }) => item.workTypeCategory !== 'EPIC';

export interface BacklogResult {
  /** Top-level items, with children nested under a parent that is also in the backlog. */
  items: Task[];
  /** Every backlog item, nested children included. */
  totalElements: number;
}

/** Work items not in any sprint. Not paginated — the whole backlog in one go. */
export const getBacklog = async (projectUuid: string): Promise<BacklogResult> => {
  try {
    const { data } = await axiosInstance.get<{ response: PaginatedTasksResponse[] }>(
      `/pmo/project/${projectUuid}/backlog`,
    );
    const result = data?.response?.[0];
    if (!result || result.status === false) {
      throw new BusinessError(result?.message || 'Could not load the backlog');
    }
    // An Epic's nested items stay in the Backlog in its place. totalElements already leaves Epics out.
    const items = (result.data ?? []).flatMap((item) => (isNotEpic(item) ? [item] : item.childWorkItems ?? []));
    return { items, totalElements: result.totalElements ?? 0 };
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** Moves work items into a sprint, from the backlog or from another sprint. */
export const addWorkItemsToSprint = async (payload: SprintWorkItemsPayload): Promise<void> => {
  try {
    await axiosInstance.post('/pmo/sprint/add-work-items', payload);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** Sends work items in a sprint back to the backlog. */
export const removeWorkItemsFromSprint = async (payload: SprintWorkItemsPayload): Promise<void> => {
  try {
    await axiosInstance.post('/pmo/sprint/remove-work-items', payload);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** Moves a PLANNING sprint to ACTIVE. Only one sprint per project can be active. */
export const startSprint = async ({ sprintUuid, ...body }: StartSprintPayload): Promise<void> => {
  try {
    await axiosInstance.post(`/pmo/sprint/${sprintUuid}/start`, body);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Work items in a sprint, as a flat list — children are not nested, and the
 * count fields are not filled in, so callers group by
 * `workItemParentWorkItemUuid` themselves.
 */
export const getSprintWorkItems = async (
  projectUuid: string,
  sprintUuid: string,
): Promise<Task[]> => {
  try {
    const { data } = await axiosInstance.get<{ response: PaginatedTasksResponse[] }>(
      `/pmo/sprint/${sprintUuid}/work-items`,
      { params: { projectUuid } },
    );
    const result = data?.response?.[0];
    if (!result || result.status === false) {
      throw new BusinessError(result?.message || 'Could not load the sprint');
    }
    return (result.data ?? []).filter(isNotEpic);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * A sprint's work items split into done and not done, with totals. For a
 * completed sprint, each unfinished item says where it was moved.
 */
export const getSprintReport = async (sprintUuid: string): Promise<SprintReport> => {
  try {
    const { data } = await axiosInstance.get<{ response: SprintReport }>(
      `/pmo/sprint/${sprintUuid}/report`,
    );
    const report = data?.response as SprintReport & { status?: unknown; message?: string };
    if (!report || report.status === false) {
      throw new BusinessError(report?.message || 'Could not load the sprint report');
    }
    // A sprint closed before Epics left sprints may still list one. Its counts already leave Epics out.
    return {
      ...report,
      completedItems: (report.completedItems ?? []).filter(isNotEpic),
      notCompletedItems: (report.notCompletedItems ?? []).filter(isNotEpic),
    };
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** Work items, subtasks and story points per assignee, plus an unassigned bucket. */
export const getSprintWorkload = async (sprintUuid: string): Promise<SprintWorkload> => {
  try {
    const { data } = await axiosInstance.get<{ response: SprintWorkload }>(
      `/pmo/sprint/${sprintUuid}/workload`,
    );
    const workload = data?.response as SprintWorkload & { status?: unknown; message?: string };
    if (!workload || workload.status === false) {
      throw new BusinessError(workload?.message || 'Could not load the sprint workload');
    }
    return { ...workload, members: workload.members ?? [] };
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Completes the ACTIVE sprint. Done items stay in it; unfinished ones move as
 * listed in `incompleteItemMoves`, or to the backlog.
 */
export const completeSprint = async (payload: CompleteSprintPayload): Promise<void> => {
  try {
    await axiosInstance.post('/pmo/sprint/complete', payload);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/** Saves the order of the project's PLANNING sprints; send all of them, top first. */
export const reorderSprints = async (payload: ReorderSprintsPayload): Promise<void> => {
  try {
    await axiosInstance.post('/pmo/sprint/reorder', payload);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

/**
 * Deletes a PLANNING or ACTIVE sprint. Its work items, done ones included, go
 * back to the backlog; none is deleted.
 */
export const deleteSprint = async ({
  projectUuid,
  sprintUuid,
}: DeleteSprintPayload): Promise<DeleteSprintResult> => {
  try {
    const { data } = await axiosInstance.delete<{
      response: DeleteSprintResult & { status?: boolean; message?: string };
    }>(`/pmo/project/${projectUuid}/delete-sprint/${sprintUuid}`);
    if (data?.response?.status === false) {
      throw new BusinessError(data.response.message || 'Could not delete the sprint');
    }
    return { movedToBacklogCount: data?.response?.movedToBacklogCount ?? 0 };
  } catch (error) {
    throw normalizeApiError(error);
  }
};
